import React, { useState } from 'react';
import { Form, Input, InputNumber, Button, Typography, Row, Col, Card, Empty, Drawer, message, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, CheckCircleOutlined, ArrowRightOutlined, PictureOutlined, PlayCircleOutlined, DownOutlined, RightOutlined, DollarOutlined } from '@ant-design/icons';
import SimpleImageDropzone from './SimpleImageDropzone';
import { videoUrlValidator } from '../../../utils/videoUtils';
import { computeMinIncrement } from '../../../utils/pricing';

const { TextArea } = Input;
const { Title, Text } = Typography;

const LotCard = ({ lot, onDelete }) => (
    <Card
        style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid #e8f4f8' }}
        bodyStyle={{ padding: 12 }}
        cover={
            <div style={{ height: 140, background: '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {lot.previewUrl ? (
                    <img src={lot.previewUrl} alt={lot.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <PictureOutlined style={{ fontSize: 36, color: '#91caff' }} />
                )}
            </div>
        }
    >
        <Text strong style={{ display: 'block', fontSize: 15, marginBottom: 4 }} ellipsis>
            {lot.title}
        </Text>
        <Text style={{ color: '#389e0d', fontWeight: 700, fontSize: 15 }}>Rs {Number(lot.starting_price).toLocaleString()}</Text>
        <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>Qty: {lot.quantity}</Text>
        <Popconfirm title="Remove this lot?" onConfirm={() => onDelete(lot)} okText="Yes" cancelText="No">
            <Button danger type="text" size="small" icon={<DeleteOutlined />} style={{ marginTop: 8, padding: 0 }}>
                Remove
            </Button>
        </Popconfirm>
    </Card>
);

const WizardLotsStep = ({ auctionTitle, lots, onAddLot, onDeleteLot, addingLot, onFinish }) => {
    const [form] = Form.useForm();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [photos, setPhotos] = useState([]);
    const [showMore, setShowMore] = useState(false);
    const startingPriceValue = Form.useWatch('starting_price', form);
    const minIncrement = computeMinIncrement(startingPriceValue);

    const openDrawer = () => {
        form.resetFields();
        setPhotos([]);
        setShowMore(false);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        if (addingLot) return;
        setDrawerOpen(false);
    };

    const handleSave = async (values) => {
        const ok = await onAddLot({ ...values, photos });
        if (ok) {
            setDrawerOpen(false);
            form.resetFields();
            setPhotos([]);
        }
    };

    const handleFinishClick = () => {
        if (lots.length === 0) {
            message.warning('Add at least one lot before finishing');
            return;
        }
        onFinish();
    };

    return (
        <div>
            <Title level={3} style={{ textAlign: 'center', marginBottom: 4 }}>
                📦 Step 2: Add your lots
            </Title>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 24, fontSize: 16 }}>
                Add each lot to "{auctionTitle}"
            </Text>

            {lots.length === 0 ? (
                <Empty description="No lots added yet" style={{ marginBottom: 24 }} />
            ) : (
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    {lots.map((lot) => (
                        <Col xs={12} sm={8} md={6} key={lot.id}>
                            <LotCard lot={lot} onDelete={onDeleteLot} />
                        </Col>
                    ))}
                </Row>
            )}

            <Button
                type="dashed"
                block
                size="large"
                icon={<PlusOutlined />}
                onClick={openDrawer}
                style={{ height: 64, fontSize: 17, fontWeight: 600, borderRadius: 14, borderWidth: 2, marginBottom: 24 }}
            >
                Add a Lot
            </Button>

            <Button
                type="primary"
                block
                size="large"
                icon={<ArrowRightOutlined />}
                onClick={handleFinishClick}
                style={{
                    height: 56,
                    fontSize: 18,
                    fontWeight: 700,
                    borderRadius: 14,
                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                    border: 'none'
                }}
            >
                Finish {lots.length > 0 ? `(${lots.length} lot${lots.length > 1 ? 's' : ''})` : ''}
            </Button>

            <Drawer
                title={<span style={{ fontSize: 18, fontWeight: 700 }}>Add a Lot</span>}
                placement="bottom"
                height="90%"
                open={drawerOpen}
                onClose={closeDrawer}
                closable={!addingLot}
                maskClosable={!addingLot}
            >
                <Form form={form} layout="vertical" requiredMark={false} onFinish={handleSave}>
                    <Form.Item label={<span style={{ fontSize: 16, fontWeight: 600 }}>📷 Photos</span>}>
                        <SimpleImageDropzone files={photos} onChange={setPhotos} />
                    </Form.Item>

                    <Form.Item
                        name="title"
                        label={<span style={{ fontSize: 16, fontWeight: 600 }}>What is this lot?</span>}
                        rules={[{ required: true, message: 'Please type a title' }]}
                    >
                        <Input size="large" placeholder="e.g. Brown Goat #1" style={{ height: 48, borderRadius: 12, fontSize: 16 }} />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={<span style={{ fontSize: 16, fontWeight: 600 }}>Describe it</span>}
                        rules={[{ required: true, message: 'Please write a short description' }]}
                    >
                        <TextArea rows={2} placeholder="e.g. 1 year old, healthy, good weight" style={{ borderRadius: 12, fontSize: 16 }} />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="starting_price"
                                label={<span style={{ fontSize: 16, fontWeight: 600 }}>Starting Price (Rs)</span>}
                                rules={[{ required: true, message: 'Enter a price' }]}
                            >
                                <InputNumber
                                    min={1}
                                    style={{ width: '100%', height: 48, fontSize: 16, borderRadius: 12 }}
                                    placeholder="e.g. 50000"
                                    onChange={() => form.validateFields(['default_increment'])}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="quantity"
                                label={<span style={{ fontSize: 16, fontWeight: 600 }}>Quantity</span>}
                                initialValue={1}
                                rules={[{ required: true, message: 'Enter a quantity' }]}
                            >
                                <InputNumber min={1} style={{ width: '100%', height: 48, fontSize: 16, borderRadius: 12 }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div style={{ marginBottom: 20 }}>
                        <button
                            type="button"
                            onClick={() => setShowMore((prev) => !prev)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                cursor: 'pointer',
                                color: '#1890ff',
                                fontSize: 15,
                                fontWeight: 600
                            }}
                        >
                            {showMore ? <DownOutlined /> : <RightOutlined />}
                            More options (optional)
                        </button>

                        {showMore && (
                            <div style={{ marginTop: 16, padding: 16, background: '#fafafa', borderRadius: 12 }}>
                                <Form.Item
                                    name="default_increment"
                                    label={<span style={{ fontSize: 15, fontWeight: 600 }}><DollarOutlined /> Bid Increment (Rs)</span>}
                                    dependencies={['starting_price']}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value) return Promise.resolve();
                                                const min = computeMinIncrement(getFieldValue('starting_price'));
                                                if (value < min) {
                                                    return Promise.reject(`Must be at least Rs ${min} (5% of the starting price)`);
                                                }
                                                return Promise.resolve();
                                            }
                                        })
                                    ]}
                                    extra={
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            How much each bid goes up by. Minimum is 5% of the starting price (Rs {minIncrement}). Leave blank to use that automatically.
                                        </Text>
                                    }
                                >
                                    <InputNumber
                                        min={minIncrement}
                                        style={{ width: '100%', height: 48, fontSize: 16, borderRadius: 12 }}
                                        placeholder={`e.g. ${minIncrement}`}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="video_url"
                                    label={<span style={{ fontSize: 15, fontWeight: 600 }}><PlayCircleOutlined /> Video Link</span>}
                                    rules={[{ validator: videoUrlValidator }]}
                                    extra={<Text type="secondary" style={{ fontSize: 12 }}>Paste a YouTube, Vimeo or TikTok link to show this lot in action</Text>}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input size="large" placeholder="e.g. https://youtube.com/watch?v=..." style={{ height: 48, borderRadius: 12, fontSize: 16 }} />
                                </Form.Item>
                            </div>
                        )}
                    </div>

                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={addingLot}
                        size="large"
                        icon={<CheckCircleOutlined />}
                        style={{ height: 52, fontSize: 17, fontWeight: 700, borderRadius: 14, marginTop: 8 }}
                    >
                        Save Lot
                    </Button>
                </Form>
            </Drawer>
        </div>
    );
};

export default WizardLotsStep;
